import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';

import { Select } from '../inputs/Select';
import { PeriodStep } from './PeriodStep';
import { formatCalendarFull, formatCalendarMonth, formatDateOnly } from '../../lib/formatDate';
import {
  addDays,
  addMonths,
  addMonthsToDate,
  buildMonthGrid,
  monthOf,
  todayCalendarDate,
  WEEKDAY_LABELS,
} from './monthGrid';

/**
 * The one month calendar. A header naming the chosen day and the month on show, a row of
 * weekday headings, and a Sunday-first grid of days.
 *
 * **It works in calendar dates and nothing else** — `YYYY-MM-DD` in, `YYYY-MM-DD` out. A day
 * on a calendar is not an instant: it names a day, not a moment, so nothing here is parsed
 * as one or converted to a zone. This is the same rule `formatDateOnly` carries, and on a
 * grid the cost of getting it wrong is not one wrong label but every cell one day out.
 *
 * **The selected day takes the product's selection treatment, not a brand-coloured fill.**
 * A tinted circle with a `slate-400` edge and a `slate-900` number — copied from
 * `<IndexPagination>`'s current page, which is the closest thing the product already has: a
 * grid of numbers where one of them is where you are. Two reasons it is not the yellow:
 * brand yellow marks the primary action and chrome the product owns, never a record's own
 * state, and white on `brand-500` is the least readable pair in the product at 2.16:1. The
 * border is what makes the circle a visible shape — `bg-blue-50` alone is 1.05:1 on white
 * and would be a selection the reader cannot find.
 *
 * **Today is the number's own weight, not a third circle.** `font-medium text-slate-900`,
 * so it composes with the selected ring instead of competing for it: a bold black number
 * inside a tinted circle is today AND selected, and a bold black number on its own is today
 * while the reader is looking at some other day. A second background tint would have had to
 * lose to the selection and today would vanish on the day it is most likely to be chosen.
 *
 * Keyboard: the grid is **one tab stop** with a roving tabindex, arrow keys to move, Home
 * and End to the ends of the week, PageUp and PageDown to change month, and Enter or Space
 * to choose. `role="grid"` is declared because all of that is implemented — the tree's rule
 * about not promising a pattern you have not built applies here too, and the answer for a
 * date grid is to build it rather than to drop the role: 42 tab stops is not navigation.
 */
/**
 * Month names in the viewer's locale — from `Intl`, like every other date string here, rather
 * than a hard-coded English array. Built once: the list is the same for every calendar on the
 * page and rebuilding it per render is twelve formatter calls for a value that cannot change.
 */
const MONTHS = Array.from({ length: 12 }, (_, index) =>
  new Intl.DateTimeFormat(undefined, { month: 'long' }).format(new Date(2000, index, 1, 12)),
);

/**
 * Whether a month has no selectable day at all.
 *
 * Walks the month rather than testing its ends, because a blackout predicate can refuse the
 * 1st and the 31st and still leave the 12th open — testing the boundaries would disable a
 * month the reader can legitimately reach.
 */
function monthIsUnavailable(month: string, isUnavailable: (date: string) => boolean): boolean {
  const days = new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)), 0).getDate();

  for (let day = 1; day <= days; day += 1) {
    if (!isUnavailable(`${month}-${String(day).padStart(2, '0')}`)) {
      return false;
    }
  }

  return true;
}

export function Calendar({
  value,
  onChange,
  month: monthProp,
  onMonthChange,
  marks,
  isDateDisabled,
  min,
  max,
  navigable = true,
  size = 'picker',
  renderDay,
  className = '',
}: {
  /** The selected calendar date, `YYYY-MM-DD`. Nothing selected when absent. */
  value?: string | null;
  /** Omit for a read-only calendar — no cell is then a button. */
  onChange?: (date: string) => void;
  /**
   * The month on show, `YYYY-MM`. Uncontrolled from `value` — or today, when there is no
   * value — when absent.
   */
  month?: string;
  onMonthChange?: (month: string) => void;
  /**
   * Dates carrying something, each marked with a dot — and **what the dot means**, in the
   * caller's own words.
   *
   * One object rather than two props, so the dates cannot be supplied without saying what
   * they signify: the label is the cell's accessible name for the mark, and a dot with no
   * name is a fact only a sighted reader gets. It is the caller's word because this
   * component has no schema behind it to take a noun from — see "Never invent terminology".
   * The same shape `<Modal>`'s `confirm` and `autogenerate` props take.
   */
  marks?: { dates: readonly string[]; label: string };
  /**
   * A date that cannot be chosen for a reason only the caller knows — a blackout, a clash.
   * Struck through, not hidden. Bounds are `min`/`max`; this is for everything else.
   */
  isDateDisabled?: (date: string) => boolean;
  /**
   * Earliest and latest selectable calendar date, `YYYY-MM-DD`.
   *
   * **Props rather than something the caller folds into `isDateDisabled`**, because they now
   * bound two things that have to agree: which days are struck through, and which years the
   * picker offers. A caller that expressed them as a predicate could disable the days and
   * still leave 1890 in the year list.
   */
  min?: string;
  max?: string;
  /**
   * Whether to offer month navigation at all. `false` drops the chevrons and the page keys,
   * for a calendar that reports one month rather than letting the reader move through them.
   *
   * It only ever takes navigation away. Passing `true` beside a `month` the caller controls
   * and does not listen to still renders nothing — see `canChangeMonth`.
   */
  navigable?: boolean;
  /**
   * `picker` (the default) is the date chooser every `<DateField>` opens. `page` is the same
   * calendar as a SCHEDULE — tall day cells that carry what falls on them, for a surface whose
   * subject is the month rather than one date in it (Building Management's calendar).
   *
   * **One component, two sizes, never a second calendar**: the grid, the week's start, the
   * month and year pickers and the keyboard model are this file's, and a schedule view written
   * beside it would be a second answer to every one of them. What `page` changes is the cell's
   * height and the footer — a schedule has no selection for the footer to state.
   */
  size?: 'picker' | 'page';
  /**
   * What a day CARRIES on a `page` calendar, under its number. Ignored on a picker, whose cell is
   * a single number by design. The content owns its own controls and their names; the cell
   * already names the date.
   */
  renderDay?: (date: string) => ReactNode;
  className?: string;
}) {
  const isPage = size === 'page';

  const today = todayCalendarDate();
  const headingId = useId();
  const gridRef = useRef<HTMLDivElement>(null);

  const [ownMonth, setOwnMonth] = useState(() => monthOf(value ?? today));
  const month = monthProp ?? ownMonth;

  /*
    Whether the month can actually move, which is not the same question as whether the
    caller asked for chevrons.

    A `month` prop with no `onMonthChange` beside it is a month the PARENT owns and has no
    way to be told about: the component can set its own state all it likes and the prop
    wins on the next render. Offering the chevrons there draws two buttons that do nothing,
    and the page keys join them — which is the dead affordance the product's hide-don't-
    disable rule exists to prevent. So navigation is derived from whether the request can be
    honoured, not from whether it was invited.
  */
  const canChangeMonth = navigable && (monthProp === undefined || onMonthChange !== undefined);

  // The cell that owns the tab stop. It starts on the selected day — or today, or the 1st —
  // so a reader tabbing in lands somewhere meaningful rather than on the borrowed day the
  // grid happens to open with.
  const [focused, setFocused] = useState(
    () => value ?? (monthOf(today) === month ? today : `${month}-01`),
  );

  // Whether a key moved the focus, as opposed to the month changing underneath it. Only the
  // first may pull DOM focus: moving it on mount would steal it from whatever opened the
  // calendar, and moving it on a month change would drag the reader out of the chevron they
  // are still clicking.
  const shouldFocusCell = useRef(false);

  // Bounds and the caller's own predicate, resolved once. Every surface that asks whether a
  // date can be chosen — the grid, the month list, the year list — asks this.
  const isUnavailable = useCallback(
    (date: string) =>
      (min !== undefined && date < min) ||
      (max !== undefined && date > max) ||
      (isDateDisabled?.(date) ?? false),
    [min, max, isDateDisabled],
  );

  /*
    The years the picker offers.

    Bounded by `min`/`max` where the caller gave them — which is what makes this worth having
    on a date of birth: `max={today}` alone turns the list into "this year back to 1906" with
    no future year in it to scroll past. With no bounds it is a window around today, wide
    enough for a birth date backwards and a planning horizon forwards.

    The month on show is always included, whatever the bounds say. A picker whose value is not
    among its own options falls back to its placeholder, so a controlled month outside the
    range would leave the reader looking at "Select…" over a grid plainly showing a year.
  */
  const years = useMemo(() => {
    const shown = Number(month.slice(0, 4));
    const thisYear = Number(today.slice(0, 4));
    const from = Math.min(min ? Number(min.slice(0, 4)) : thisYear - 120, shown);
    const to = Math.max(max ? Number(max.slice(0, 4)) : thisYear + 20, shown);

    return Array.from({ length: to - from + 1 }, (_, i) => from + i);
  }, [month, today, min, max]);

  const cells = useMemo(() => buildMonthGrid(month), [month]);
  const markedDates = useMemo(() => new Set(marks?.dates ?? []), [marks]);

  // A focused date outside the month on show cannot own a tab stop that no cell carries, so
  // it is pulled back to the month. Derived rather than set in an effect, which would leave
  // one render with no tab stop in the grid at all.
  const focusedInMonth = monthOf(focused) === month ? focused : `${month}-01`;

  useEffect(() => {
    if (!shouldFocusCell.current) {
      return;
    }

    shouldFocusCell.current = false;
    gridRef.current?.querySelector<HTMLElement>(`[data-date="${focusedInMonth}"]`)?.focus();
  }, [focusedInMonth]);

  function goToMonth(next: string) {
    setOwnMonth(next);
    onMonthChange?.(next);
  }

  function moveFocus(next: string) {
    const leavesMonth = monthOf(next) !== month;

    // Where the month cannot move, focus stops at the edge of the grid rather than stepping
    // onto a date no cell is drawing. Moving it anyway put the tab stop on a cell that does
    // not exist, and the clamp below then dropped the reader on the 1st — an arrow key that
    // jumps a fortnight backwards reads as the grid losing its place.
    if (leavesMonth && !canChangeMonth) {
      return;
    }

    shouldFocusCell.current = true;
    setFocused(next);

    if (leavesMonth) {
      goToMonth(monthOf(next));
    }
  }

  function onKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    const current = focusedInMonth;

    switch (event.key) {
      case 'ArrowLeft':
        moveFocus(addDays(current, -1));
        break;
      case 'ArrowRight':
        moveFocus(addDays(current, 1));
        break;
      case 'ArrowUp':
        moveFocus(addDays(current, -7));
        break;
      case 'ArrowDown':
        moveFocus(addDays(current, 7));
        break;
      case 'Home':
        // The ends of the reader's own week, found from the grid rather than by arithmetic
        // on a weekday number — the grid already knows where the row starts.
        moveFocus(weekEdge(cells, current, 'start'));
        break;
      case 'End':
        moveFocus(weekEdge(cells, current, 'end'));
        break;
      case 'PageUp':
        if (!canChangeMonth) return;
        moveFocus(addMonthsToDate(current, -1));
        break;
      case 'PageDown':
        if (!canChangeMonth) return;
        moveFocus(addMonthsToDate(current, 1));
        break;
      default:
        return;
    }

    // Only reached when a key above matched. Arrows and page keys scroll the panel behind
    // the calendar otherwise, so the grid would move and the page would move with it.
    event.preventDefault();
  }

  return (
    <div
      className={`overflow-hidden rounded-panel border border-slate-300 bg-white shadow-panel ${className}`}
    >
      {/*
        Two rows, and the order is the point: **what you are changing on top, what you have
        chosen underneath.**

        They shared one line while the month was a plain label. Two selects and the chevrons do
        not leave room for a day beside them — "Wednesday 29th" clipped to "Wednesda…", which is
        the one string in this panel a reader needs whole, and widening the panel to fit both
        only moved the point at which it happened. Giving the day its own row removes the
        competition instead of paying for it.

        The day is also the QUIETER of the two now: it reports what is already circled in the
        grid below, where the selects are the controls that move it. Small and muted says that.
      */}
      <div className="flex items-center justify-between gap-2 border-b border-slate-200 px-5 py-3">
        {/*
            The grid's accessible name, always present and always the month in words. When the
            pickers render it is `sr-only`: two selects announce their own values, and reading
            "September 2026" a third time before them is the same value in three slots.
          */}
        <p
          id={headingId}
          className={canChangeMonth ? 'sr-only' : 'font-mono text-sm text-slate-900'}
        >
          {formatCalendarMonth(month)}
        </p>

        {/*
            Month and year are CHOSEN, not only stepped to.

            The chevrons stay — they are the right control for "the month before this one", and
            they are one click where the select is three. What they cannot do is cross thirty
            years: a date of birth reached through them is six hundred clicks, which is the gap
            this closes and the reason `<DateField>` warned against a picker-only date box.

            A month with no selectable day in it is `disabled` rather than hidden — disabled for
            STATE, so the list keeps its shape and the reader can see the bound rather than
            wondering why the year starts in April.
          */}
        {canChangeMonth && (
          /*
              Each picker is in a SIZED wrapper, not sized by its own `className` — that prop
              lands on `<Select>`'s button, and the component's root still grew to fill the row.
              A width that silently does nothing is worse than none: it reads as decided.
            */
          <div className="flex items-center gap-2">
            <div className="w-40">
              <Select
                id={`${headingId}-month`}
                ariaLabel="Month"
                compact
                clearable={false}
                sort="none"
                value={String(Number(month.slice(5, 7)))}
                onChange={(next) => goToMonth(`${month.slice(0, 4)}-${next.padStart(2, '0')}`)}
                options={MONTHS.map((name, index) => {
                  const candidate = `${month.slice(0, 4)}-${String(index + 1).padStart(2, '0')}`;
                  return {
                    value: String(index + 1),
                    label: name,
                    disabled: monthIsUnavailable(candidate, isUnavailable),
                  };
                })}
              />
            </div>

            <div className="w-28">
              <Select
                id={`${headingId}-year`}
                ariaLabel="Year"
                compact
                clearable={false}
                sort="none"
                value={month.slice(0, 4)}
                onChange={(next) => goToMonth(`${next}-${month.slice(5, 7)}`)}
                options={years.map((year) => ({
                  value: String(year),
                  label: String(year),
                }))}
              />
            </div>
          </div>
        )}

        {/*
            The one addition to the layout. A calendar with no way out of its month is a
            display rather than a control — and where it genuinely is a display, `navigable`
            is false and nothing renders, which is the product's rule about a control that
            can never do anything not being drawn at all.
          */}
        {canChangeMonth && (
          <PeriodStep
            previousLabel="Previous month"
            nextLabel="Next month"
            onPrevious={() => goToMonth(addMonths(month, -1))}
            onNext={() => goToMonth(addMonths(month, 1))}
          />
        )}
      </div>

      <div
        ref={gridRef}
        role="grid"
        aria-labelledby={headingId}
        onKeyDown={onKeyDown}
        className={isPage ? '' : 'px-3 pb-4 pt-2'}
      >
        <div role="row" className="grid grid-cols-7">
          {WEEKDAY_LABELS.map((label) => (
            <div
              key={label}
              role="columnheader"
              className="py-2 text-center text-xs font-semibold tracking-wide text-slate-500"
            >
              {label}
            </div>
          ))}
        </div>

        {chunkWeeks(cells).map((week) => (
          <div role="row" key={week[0].date} className="grid grid-cols-7">
            {week.map((cell) => {
              const isSelected = cell.date === value;
              const disabled = isUnavailable(cell.date);

              return (
                <div
                  role="gridcell"
                  key={cell.date}
                  aria-selected={isSelected}
                  className={
                    isPage
                      ? /*
                          A SCHEDULE cell: the number top-left and the day's content under it.
                          `min-w-0` so a long entry truncates inside its column rather than
                          widening the week; the rules are the grids' one tone, slate-200.
                        */
                        `flex min-h-28 min-w-0 flex-col gap-1 border-t border-slate-200 p-1.5 [&:not(:first-child)]:border-l ${cell.inMonth ? '' : 'bg-slate-50'}`
                      : 'flex justify-center py-1'
                  }
                >
                  <DayCell
                    compact={isPage}
                    cell={cell}
                    isSelected={isSelected}
                    isToday={cell.date === today}
                    marked={markedDates.has(cell.date)}
                    markLabel={marks?.label ?? ''}
                    disabled={disabled}
                    tabIndex={cell.date === focusedInMonth ? 0 : -1}
                    onSelect={
                      /*
                        `onChange` alone, never `onChange && !disabled`. The two are
                        different questions — whether the calendar is interactive at all,
                        and whether THIS date can be picked — and folding them together sent
                        every unavailable day down the read-only branch, where it rendered as
                        a `<span>`: struck through so it looked unavailable, and carrying
                        none of the semantics that say so to a screen reader. The `disabled`
                        attribute on the button is what refuses the click.
                      */
                      onChange
                        ? () => {
                            onChange(cell.date);
                            setFocused(cell.date);

                            // Choosing a borrowed day moves the month to it. The reader has
                            // picked a date in the next month; leaving the grid where it was
                            // would show their selection as a faint number in the margin.
                            if (!cell.inMonth) {
                              goToMonth(monthOf(cell.date));
                            }
                          }
                        : undefined
                    }
                  />

                  {isPage ? renderDay?.(cell.date) : null}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/*
        No footer on a SCHEDULE: it states the selection, and a page calendar has none to state.
      */}
      {/*
        The footer states the WHOLE date, and that is why it exists.

        The header used to carry "Thursday 9th" under the pickers. Once the month and year
        became controls, that label named the two parts the reader could still see and dropped
        the two they had just changed — and a reader who pages to another month to look around
        needs to be told what is still selected, not what month they are browsing.

        Its own region, under a rule, so it reads as the panel's summary rather than as a caption
        on the grid. Muted and mono: it reports the state, it does not offer anything.
      */}
      {isPage ? null : (
        <div className="border-t border-slate-200 px-5 py-3">
          <p className="truncate font-mono text-xs text-slate-500">{formatCalendarFull(value)}</p>
        </div>
      )}
    </div>
  );
}

/**
 * One day. A circle the size of the touch target rather than a cell-wide block: the grid's
 * subject is a single day, and a full-width highlight reads as a range.
 *
 * The number is `font-mono tabular-nums` and zero-padded, like every other machine value in
 * the product — and here it is also what keeps the columns straight, since a proportional 1
 * is narrower than a 0 and a column of mixed widths wanders.
 */
function DayCell({
  compact = false,
  cell,
  isSelected,
  isToday,
  marked,
  markLabel,
  disabled,
  tabIndex,
  onSelect,
}: {
  /** The schedule's cell — a smaller number, set top-left above the day's content. */
  compact?: boolean;
  cell: { date: string; day: number; inMonth: boolean };
  isSelected: boolean;
  isToday: boolean;
  marked: boolean;
  markLabel: string;
  disabled: boolean;
  tabIndex: number;
  onSelect?: () => void;
}) {
  /*
    The accessible name. `markLabel` is the CALLER's word, never one invented here: the dot
    means whatever the surface drawing it says it means, and this component has no schema
    behind it to take a noun from. It read "has activity" first, which named no field and
    answered no question a reader has.
  */
  const label = [formatDateOnly(cell.date), isToday ? '(today)' : '', marked ? markLabel : '']
    .filter(Boolean)
    .join(' ');

  // Hover belongs to a cell that can be clicked. A read-only calendar tinting cells under
  // the pointer says "this one is live" about something that does nothing — the same reason
  // a hover state is never a substitute for the hand cursor, read the other way round.
  const hover = onSelect && !disabled ? 'hover:bg-slate-100' : '';

  const tone = disabled
    ? // Struck through as well as faint, because "outside this month" is already the faint
      // treatment and the reader has to be able to tell a day in the margin from one they
      // are not allowed to pick.
      'text-slate-300 line-through decoration-slate-300'
    : isSelected
      ? 'border border-slate-400 bg-blue-50 font-medium text-slate-900'
      : isToday
        ? 'font-medium text-slate-900'
        : cell.inMonth
          ? 'text-slate-700'
          : 'text-slate-400';

  const box = compact ? 'h-7 w-7 shrink-0 text-xs' : 'h-9 w-9 text-sm';
  const classes = `relative flex ${box} items-center justify-center rounded-full font-mono tabular-nums transition ${tone} ${hover}`;
  const number = `${cell.day}`.padStart(2, '0');

  // No handler is what makes a calendar read-only — a disabled button on every cell would be
  // 42 dead affordances, and the rule is to hide rather than disable.
  if (!onSelect) {
    return (
      <span className={classes} data-date={cell.date}>
        {/*
          An `sr-only` twin rather than an `aria-label`, which ARIA does not guarantee on a
          generic element — on a roleless `<span>` it is free to be dropped, and a read-only
          calendar then announces 35 bare numbers with no month or year behind them. The
          pairing is the one the shared inputs already use for a `<HelpTip>` description.
        */}
        <span aria-hidden="true">{number}</span>
        <span className="sr-only">{label}</span>
        {marked && <EventDot />}
      </span>
    );
  }

  return (
    <button
      type="button"
      data-date={cell.date}
      tabIndex={tabIndex}
      disabled={disabled}
      aria-label={label}
      onClick={onSelect}
      className={`${classes} disabled:cursor-not-allowed disabled:hover:bg-transparent`}
    >
      {number}
      {marked && <EventDot />}
    </button>
  );
}

/**
 * The marker for a day carrying something. `blue-500` is 3.68:1 on white, which clears WCAG
 * 1.4.11's 3:1 for a graphic that conveys meaning — and it has to, because the dot is the
 * only thing saying so. The brand yellow would not: this is a fact about the day, which is
 * exactly what the yellow does not get to mark.
 *
 * `aria-hidden`, because the cell's own accessible name already says it. Two announcements
 * for one dot is the same value in two slots.
 */
function EventDot() {
  return (
    <span
      aria-hidden="true"
      className="absolute right-1.5 top-1.5 h-1.5 w-1.5 rounded-full bg-blue-500"
    />
  );
}


function chunkWeeks<T>(cells: readonly T[]): T[][] {
  return Array.from({ length: Math.ceil(cells.length / 7) }, (_, week) =>
    cells.slice(week * 7, week * 7 + 7),
  );
}

/** The first or last date of the week row holding `date`. */
function weekEdge(cells: readonly { date: string }[], date: string, edge: 'start' | 'end'): string {
  const index = cells.findIndex((cell) => cell.date === date);

  if (index < 0) {
    return date;
  }

  const row = Math.floor(index / 7);
  return cells[row * 7 + (edge === 'start' ? 0 : 6)].date;
}
